import React, { useState, useEffect } from "react";
import { Form, Dropdown, Button, Checkbox, List } from "semantic-ui-react";
import { map, filter, includes, sumBy } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCategory, useProduct, useOffer } from "../../../../hooks";
import "./AddEditOfferForm.scss";

export function AddEditOfferForm(props) {
  const { onClose, onRefetch, offer } = props;
  const [categoriesFormat, setCategoriesFormat] = useState([]);
  const [productsFormat, setProductsFormat] = useState([]);
  const { categories, getCategories } = useCategory();
  const { products, getProducts } = useProduct();
  const { addOffer, updateOffer } = useOffer();

  useEffect(() => {
    getCategories();
    getProducts();
  }, []);

  useEffect(() => {
    setCategoriesFormat(
      map(categories, (item) => ({
        key: item.id,
        text: item.title,
        value: item.id,
      }))
    );
  }, [categories]);

  useEffect(() => {
    setProductsFormat(
      map(products, (item) => ({
        key: item.id,
        text: `${item.title} (S/ ${item.price})`,
        value: item.id,
      }))
    );
  }, [products]);

  const formik = useFormik({
    initialValues: initialValues(offer),
    validationSchema: Yup.object(schema()),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (offer) await updateOffer(offer.id, formValue);
      else await addOffer(formValue);

      onRefetch();
      onClose();
    },
  });

  const selectedProducts = filter(products, (product) =>
    includes(formik.values.products, product.id)
  );
  const originalTotal = sumBy(selectedProducts, (product) =>
    Number(product.price)
  );
  const savings = originalTotal - Number(formik.values.price || 0);

  return (
    <Form className="add-edit-offer-form" onSubmit={formik.handleSubmit}>
      <Form.Input
        name="title"
        placeholder="Nombre de la oferta (ej. Combo Familiar)"
        value={formik.values.title}
        onChange={formik.handleChange}
        error={formik.errors.title}
      />

      <Dropdown
        placeholder="Categoria"
        fluid
        selection
        search
        options={categoriesFormat}
        value={formik.values.category}
        error={formik.errors.category}
        onChange={(_, data) => formik.setFieldValue("category", data.value)}
      />

      <Dropdown
        placeholder="Productos incluidos"
        fluid
        multiple
        selection
        search
        options={productsFormat}
        value={formik.values.products}
        error={formik.errors.products}
        onChange={(_, data) => formik.setFieldValue("products", data.value)}
      />

      {selectedProducts.length > 0 && (
        <List className="add-edit-offer-form__summary" bulleted>
          {map(selectedProducts, (product) => (
            <List.Item key={product.id}>
              {product.title} — S/ {product.price}
            </List.Item>
          ))}
          <List.Item className="add-edit-offer-form__summary-total">
            Total normal: S/ {originalTotal.toFixed(2)}
          </List.Item>
        </List>
      )}

      <Form.Input
        type="number"
        name="price"
        placeholder="Precio de la oferta"
        value={formik.values.price}
        onChange={formik.handleChange}
        error={formik.errors.price}
      />

      {selectedProducts.length > 0 && formik.values.price && (
        <p className="add-edit-offer-form__savings">
          {savings > 0
            ? `El cliente ahorra S/ ${savings.toFixed(2)}`
            : "El precio de la oferta no es menor al total normal"}
        </p>
      )}

      <div className="add-edit-offer-form__active">
        <Checkbox
          toggle
          checked={formik.values.active}
          onChange={(_, data) => formik.setFieldValue("active", data.checked)}
        />
        Oferta activa
      </div>

      <Button
        type="submit"
        primary
        fluid
        content={offer ? "Actualizar" : "Crear"}
      />
    </Form>
  );
}

function initialValues(data) {
  return {
    title: data?.title || "",
    category: data?.category || "",
    products: map(data?.products_data, "id") || [],
    price: data?.price || "",
    active: data ? data.active : true,
  };
}

function schema() {
  return {
    title: Yup.string().required(true),
    category: Yup.number().required(true),
    products: Yup.array().min(2, "Elige al menos 2 productos").required(true),
    price: Yup.number().required(true),
    active: Yup.boolean().required(true),
  };
}
